'use client';

import { useEffect, useMemo } from 'react';
import { useOptionalUserProfile } from '@/lib/providers/user-profile-provider';
import { observeGettingStartedCompletions } from '@/lib/guidance';
import { syncGettingStartedAttributesToIntercom } from '@/components/intercom/intercom-getting-started-attributes';
import { useIntercomAvailable } from '@/components/intercom/use-intercom-available';
import { useOnboardingStatus } from '@/lib/services/onboarding';
import { normalizeRole } from '@/lib/utils/permissions';
import {
  canReachGettingStarted,
  isGettingStartedComplete,
  TOTAL_STEPS
} from '@/lib/getting-started';
import type {
  GettingStartedStatus,
  GettingStartedStepKey
} from '@/lib/services/onboarding/types';

export interface GettingStartedProgress {
  /** The server's own answer, or null while loading / on a failed fetch. */
  status: GettingStartedStatus | null;
  /** Step keys the user has actually done. */
  completed: ReadonlySet<GettingStartedStepKey>;
  /** How many of the six are done (0–6). */
  completedCount: number;
  /** Progress percent (0–100, integer). */
  percent: number;
  /** Whether every step is done. */
  isComplete: boolean;
  /** Whether this user may reach the checklist at all. @see canReachGettingStarted */
  canReach: boolean;
  /** Whether the user asked not to be landed on the hub unprompted. */
  isDismissed: boolean;
  /** Whether the user is treated as an admin (the signature-gap banner's audience). */
  isAdmin: boolean;
  /**
   * True until the server has answered. Nothing renders a verdict before this
   * clears — there is no cached guess to fall back on, which is the point.
   */
  isLoading: boolean;
}

/**
 * The single source of truth for Getting Started progress, shared by all three
 * surfaces: the hub, the sidebar widget and the icon rail.
 *
 * **There used to be two hooks and they disagreed in two ways**, both of which
 * this collapse dissolves by construction rather than fixing (VALE-1830):
 *
 * - `use-getting-started-snapshot` read the Zustand cache only and defaulted a
 *   `null` setup-firm verdict to `false`, so the widget and the rail
 *   **under-reported by one step** for any user who had never opened the hub —
 *   the hub was the only writer of that cache.
 * - The snapshot omitted the hub's canonical-membership filter, so a stale step
 *   key left in `localStorage` counted toward the ring in one place and not the
 *   other.
 *
 * Both were artefacts of progress living in the browser. It now lives on the
 * account and every surface reads the same response, so there is one number.
 *
 * **The rail is mounted on every page in the app, and this hook fetches.** That
 * is deliberate and it costs one request: `useOnboardingStatus` is a shared
 * React Query key with a five-minute `staleTime`, so the rail, the widget and
 * the hub dedupe onto a single fetch. The previous arrangement avoided the fetch
 * by having the rail read a cache only the hub ever wrote, which is precisely
 * the bug above.
 */
export function useGettingStartedProgress(): GettingStartedProgress {
  const { profile } = useOptionalUserProfile();
  const { data, isLoading } = useOnboardingStatus();
  const intercomAvailable = useIntercomAvailable();

  const status = data?.gettingStarted ?? null;

  // `userType` is unreliable on real backend responses; `role` is consistently
  // populated and `normalizeRole` folds 'admin' / 'administrator' / etc.
  const isAdmin = normalizeRole(profile?.role) === 'admin';

  const completed = useMemo(() => {
    const done = new Set<GettingStartedStepKey>();
    for (const step of status?.steps ?? []) {
      if (step.completed) done.add(step.id);
    }
    return done;
  }, [status]);

  /*
    Report a step that becomes complete while the user is watching (VALE-1790).

    Here rather than in a surface, because this hook is the single source of
    truth all three read, so the transition is observed once however many of
    them are mounted, and the icon rail is mounted on every page. The observer
    itself holds the session baseline and the reported set, so calling it from
    three instances still emits one event; see its own doc comment for why the
    browser reports a completion the server already recorded.

    Gated on a resolved response: `status === null` is "still loading", and
    treating it as "nothing complete" would make the first real snapshot look
    like six simultaneous completions.
  */
  useEffect(() => {
    if (status === null) return;
    observeGettingStartedCompletions(completed);
  }, [status, completed]);

  /*
    Mirror the server's progress onto the Intercom contact as attributes
    (VALE-1884), so a checklist step ticks off the same record the in-app row
    reads rather than off the undercounting event above.

    Gated on the Messenger having booted, and re-run when it does: an update
    sent before boot is dropped, and the progress response usually lands first.
  */
  const userId = profile?.id;
  useEffect(() => {
    if (status === null || intercomAvailable !== true || userId === undefined) {
      return;
    }
    syncGettingStartedAttributesToIntercom(userId, status);
  }, [status, intercomAvailable, userId]);

  return {
    status,
    completed,
    completedCount: status?.completedCount ?? 0,
    // `totalCount` comes from the server so nothing hard-codes six; TOTAL_STEPS
    // is only the pre-response placeholder, and the registry test holds the two
    // to the same number.
    percent: status
      ? Math.round((status.completedCount / status.totalCount) * 100)
      : 0,
    isComplete: isGettingStartedComplete(status),
    canReach: canReachGettingStarted(status),
    isDismissed: status?.dismissed ?? false,
    isAdmin,
    isLoading: isLoading || status === null
  };
}

export { TOTAL_STEPS };
